<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { api, isAbortError } from "../api";
import { artifactUrlStateCounts, isAvailabilityActionable } from "../domain-presentation";
import type { Artifact, ArtifactTreePage, AvailabilityState } from "../types";
import AvailabilityBadge from "./AvailabilityBadge.vue";
import FragmentFileRow from "./FragmentFileRow.vue";

const props = withDefaults(defineProps<{ domainId: string; version: string; kind: string; availabilityState?: AvailabilityState; allowActions?: boolean }>(), { allowActions: false });
const page = ref<ArtifactTreePage>({ prefix: "", folders: [], items: [], next_cursor: null });
const loading = ref(true);
const loadingMore = ref(false);
const error = ref("");
const expanded = ref<number | null>(null);
let controller: AbortController | null = null;
let requestGeneration = 0;

const crumbs = computed(() => page.value.prefix.split("/").filter(Boolean));
const showsUrlDetails = computed(() => props.kind !== "file");

async function load(prefix: string, append = false): Promise<void> {
  controller?.abort();
  const request = new AbortController();
  controller = request;
  const generation = ++requestGeneration;
  append ? loadingMore.value = true : loading.value = true;
  error.value = "";
  try {
    const result = await api.artifactTree(props.domainId, props.version, {
      kind: props.kind, prefix, cursor: append ? page.value.next_cursor : null,
      state: showsUrlDetails.value ? props.availabilityState : undefined, limit: 100,
    }, request.signal);
    if (controller !== request || generation !== requestGeneration) return;
    page.value = append ? { ...result, items: [...page.value.items, ...result.items] } : result;
    if (!append) expanded.value = null;
  } catch (reason) {
    if (isAbortError(reason) || controller !== request || generation !== requestGeneration) return;
    error.value = reason instanceof Error ? reason.message : "目录加载失败";
  } finally {
    if (controller === request && generation === requestGeneration) {
      loading.value = false;
      loadingMore.value = false;
    }
  }
}

function goCrumb(index: number): void { void load(crumbs.value.slice(0, index + 1).join("/")); }
function formatBytes(value: number): string {
  const units = ["B", "KB", "MB", "GB", "TB"];
  let size = value, unit = 0;
  while (size >= 1024 && unit < units.length - 1) { size /= 1024; unit += 1; }
  return `${size.toFixed(unit ? 2 : 0)} ${units[unit]}`;
}
function filename(item: Artifact): string { return item.name.replaceAll("\\", "/").split("/").at(-1) || item.name; }
function artifactPath(item: Artifact): string {
  const normalized = item.name.replaceAll("\\", "/");
  return normalized === filename(item) ? "" : normalized;
}
function toggle(item: Artifact): void { expanded.value = expanded.value === item.id ? null : item.id; }
function candidateIsActionable(candidate: Artifact["urls"][number]): boolean { return props.allowActions && isAvailabilityActionable(candidate.current, candidate.url); }
function stateRows(item: Artifact): Array<{ state: AvailabilityState; label: string; count: number }> {
  const counts = artifactUrlStateCounts(item);
  return ([
    { state: "available", label: "可用", count: counts.available },
    { state: "unavailable", label: "失效", count: counts.unavailable },
    { state: "unknown", label: "未判定", count: counts.unknown },
  ] as Array<{ state: AvailabilityState; label: string; count: number }>).filter((row) => row.count > 0);
}

watch(() => [props.domainId, props.version, props.kind], () => void load(""));
watch(() => props.availabilityState, () => void load(page.value.prefix));
onMounted(() => void load(""));
onBeforeUnmount(() => controller?.abort());
</script>

<template>
  <div class="remote-tree">
    <div class="hoyo-browser-head"><div class="hoyo-breadcrumb"><button class="breadcrumb-step" :class="{ active: !crumbs.length }" @click="load('')">根目录</button><template v-for="(part, index) in crumbs" :key="`${part}:${index}`"><span class="breadcrumb-sep">/</span><button class="breadcrumb-step" :class="{ active: index === crumbs.length - 1 }" @click="goCrumb(index)">{{ part }}</button></template></div><div class="hoyo-browser-count"><span>当前目录</span><strong>{{ page.folders.length }} 个文件夹 / {{ page.items.length }} 个文件</strong></div></div>
    <div v-if="loading" class="empty">正在读取目录索引…</div>
    <div v-else-if="error" class="empty error-state"><strong>目录加载失败</strong><span>{{ error }}</span><button class="tool-button" type="button" @click="load(page.prefix)">重试</button></div>
    <template v-else>
      <div v-if="page.folders.length || page.items.length" class="hoyo-browser">
        <button v-for="folder in page.folders" :key="folder.path" class="browser-row folder-row" @click="load(folder.path)"><span class="browser-icon folder"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 6.5h6l2 2h10v9H3z" /></svg></span><span class="browser-name"><strong>{{ folder.name }}</strong><small>{{ folder.artifact_count }} 个文件</small></span><span class="browser-size">{{ formatBytes(folder.total_size) }}</span></button>
        <template v-for="artifact in page.items" :key="artifact.id"><FragmentFileRow v-if="!showsUrlDetails" :artifact="artifact" /><template v-else><button class="browser-row file-row" :class="{ selected: expanded === artifact.id }" @click="toggle(artifact)"><span class="browser-icon file"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 3h8l4 4v14H6zM14 3v5h5" /></svg></span><span class="browser-name"><strong>{{ filename(artifact) }}</strong><small v-if="artifactPath(artifact)">{{ artifactPath(artifact) }}</small></span><span class="browser-availability"><span v-for="row in stateRows(artifact)" :key="row.state" class="url-state" :data-state="row.state">{{ row.label }} {{ row.count }}</span></span><span class="browser-size">{{ formatBytes(artifact.size) }}</span></button><div v-if="expanded === artifact.id" class="browser-detail"><div class="browser-checksum"><strong>{{ artifact.checksum_type || 'checksum' }}: {{ artifact.checksum_value || '—' }}</strong><span>{{ artifact.urls.length }} 个 provider 候选</span></div><div class="browser-url-list"><div v-for="candidate in artifact.urls" :key="candidate.id" class="browser-url-row"><AvailabilityBadge :value="candidate.current || null" /><strong>{{ candidate.provider || candidate.source_kind || 'unknown' }}</strong><span :title="candidate.url">{{ candidate.url }}</span><a v-if="candidateIsActionable(candidate)" class="icon-button" :href="candidate.url" target="_blank" rel="noreferrer">打开</a><span v-else class="stale-link">不可操作</span></div></div></div></template></template>
      </div>
      <button v-if="page.next_cursor" class="load-more" :disabled="loadingMore" @click="load(page.prefix, true)">{{ loadingMore ? '读取中…' : '加载更多文件' }}</button>
      <div v-if="!page.folders.length && !page.items.length" class="empty">当前目录为空。</div>
    </template>
  </div>
</template>
